import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Copy, RefreshCw, Send, Loader2, KeyRound, Webhook, ScrollText, ListChecks,
  LayoutDashboard, BookOpen, LogOut, CheckCircle2, Clock, QrCode, ArrowRight, AlertTriangle, ShieldCheck, RefreshCcw,
} from "lucide-react";
import { api, apiError, timeAgo, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { StatusPill } from "@/components/Pills";
import { DashboardCharts } from "@/components/DashboardCharts";
import { TwoFactorSettings } from "@/components/TwoFactorSettings";
import { DeliveryBadge, payinItems } from "@/pages/AdminDashboard";
import { ExpandableRow } from "@/components/ExpandableRow";
import { ApiGuide } from "@/pages/ApiGuide";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const card = "rounded-xl border border-slate-700 bg-[#1E293B]";

const NAV = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "integration", label: "Integration", icon: KeyRound },
  { key: "docs", label: "API Docs", icon: BookOpen },
  { key: "submissions", label: "Submissions", icon: ListChecks },
  { key: "chargebacks", label: "Charge Backs", icon: RefreshCcw },
  { key: "logs", label: "Webhook Logs", icon: ScrollText },
  { key: "security", label: "Security", icon: ShieldCheck },
];

export default function VendorPortal() {
  const { logout } = useAuth();
  const nav = useNavigate();
  const [section, setSection] = useState("overview");
  const [me, setMe] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [subs, setSubs] = useState([]);
  const [webhook, setWebhook] = useState("");
  const [reveal, setReveal] = useState(null);
  const [busy, setBusy] = useState("");

  const load = async () => {
    try {
      const [m, d, s] = await Promise.all([
        api.get("/merchant/me"), api.get("/merchant/deliveries"), api.get("/merchant/submissions"),
      ]);
      setMe(m.data); setDeliveries(d.data); setSubs(s.data);
      setWebhook(m.data.webhook_url || "");
    } catch (e) { /* */ }
  };

  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, []);

  const doLogout = async () => { await logout(); nav("/merchant/login"); };

  const rotateKey = async () => {
    setBusy("key");
    try { const { data } = await api.post("/merchant/rotate-key"); setReveal({ label: "New API Key", value: data.api_key }); toast.success("API key rotated — old key invalidated"); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const rotateSecret = async () => {
    setBusy("secret");
    try { const { data } = await api.post("/merchant/rotate-secret"); setReveal({ label: "New Webhook Secret", value: data.webhook_secret }); toast.success("Webhook secret rotated"); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const saveWebhook = async () => {
    setBusy("wh");
    try { await api.put("/merchant/webhook", { webhook_url: webhook }); toast.success("Webhook URL saved"); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const sendTest = async () => {
    setBusy("test");
    try { await api.post("/merchant/test-webhook"); toast.success("Test webhook sent"); setSection("logs"); setTimeout(load, 800); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const resend = async (id) => {
    try { await api.post(`/merchant/deliveries/${id}/resend`); toast.success("Resent"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!me) return <div className="min-h-screen bg-[#0F172A] text-white flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const matched = subs.filter((s) => s.status === "received").length;
  const pending = subs.length - matched;
  const delivered = deliveries.filter((d) => d.status === "delivered").length;

  const ctx = { me, subs, deliveries, matched, pending, delivered, webhook, setWebhook,
    reveal, setReveal, busy, rotateKey, rotateSecret, saveWebhook, sendTest, resend, setSection };

  return (
    <div className="min-h-screen bg-[#0F172A] text-white font-sans">
      <header className="border-b border-slate-800 sticky top-0 z-20 bg-[#0F172A]/90 backdrop-blur">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo light />
            <span className="hidden sm:inline-flex items-center gap-2 text-xs text-slate-500 border-l border-slate-700 pl-3">
              <span className="font-mono text-slate-300">{me.name || me.merchant_code}</span>
              <StatusDot status={me.status} />
            </span>
          </div>
          <Button data-testid="logout-button" onClick={doLogout} variant="outline" className="border-slate-600 bg-transparent text-slate-300 hover:bg-slate-800 h-9"><LogOut className="w-4 h-4 mr-1.5" />Logout</Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 py-6 flex flex-col md:flex-row gap-6">
        <nav className="md:w-56 shrink-0">
          <div className="flex md:flex-col gap-1 overflow-x-auto pb-1 -mx-1 px-1">
            {NAV.map((n) => {
              const Icon = n.icon;
              const active = section === n.key;
              return (
                <button
                  key={n.key}
                  data-testid={`merchant-nav-${n.key}`}
                  onClick={() => setSection(n.key)}
                  className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors shrink-0 md:w-full ${active ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30" : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/60 border border-transparent"}`}
                >
                  <Icon className="w-4 h-4" /> {n.label}
                </button>
              );
            })}
          </div>
          <div className="hidden md:block mt-4 rounded-lg border border-slate-800 bg-[#1E293B]/50 p-3">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Webhook</div>
            <div className="mt-1 flex items-center gap-1.5 text-xs">
              {me.webhook_url
                ? <><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> <span className="text-emerald-400">Configured</span></>
                : <><AlertTriangle className="w-3.5 h-3.5 text-amber-400" /> <span className="text-amber-400">Not set</span></>}
            </div>
          </div>
        </nav>

        <main className="flex-1 min-w-0">
          {section === "overview" && <Overview ctx={ctx} />}
          {section === "integration" && <Integration ctx={ctx} />}
          {section === "docs" && <ApiGuide />}
          {section === "submissions" && <Submissions subs={subs} />}
          {section === "chargebacks" && <ChargeBacks />}
          {section === "logs" && <Logs deliveries={deliveries} resend={resend} />}
          {section === "security" && (
            <div className="max-w-2xl">
              <h1 className="text-xl font-bold tracking-tight mb-1 text-white">Security</h1>
              <p className="text-sm text-slate-400 mb-4">Protect your account with an authenticator app.</p>
              <TwoFactorSettings theme="dark" />
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function StatusDot({ status }) {
  const active = status === "active";
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${active ? "text-emerald-400" : "text-rose-400"}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${active ? "bg-emerald-400" : "bg-rose-400"}`} />
      {active ? "Active" : "Suspended"}
    </span>
  );
}

function Th({ children }) { return <th className="text-left text-[11px] uppercase tracking-wider text-slate-500 font-semibold px-3 py-2">{children}</th>; }
function Td({ children, mono }) { return <td className={`px-3 py-2.5 text-sm ${mono ? "font-mono text-xs" : ""}`}>{children}</td>; }
function SectionHead({ icon: Icon, title, sub }) {
  return (
    <div className="mb-4">
      <div className="flex items-center gap-2"><Icon className="w-5 h-5 text-emerald-400" /><h1 className="text-xl font-bold tracking-tight">{title}</h1></div>
      {sub && <p className="text-sm text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}

/* ---------------- Overview ---------------- */
function Overview({ ctx }) {
  const { me, subs, matched, pending, delivered, deliveries, setSection } = ctx;
  const stats = [
    { label: "Total Submissions", value: subs.length, icon: ListChecks, accent: "text-sky-400" },
    { label: "Matched", value: matched, icon: CheckCircle2, accent: "text-emerald-400" },
    { label: "Pending", value: pending, icon: Clock, accent: "text-amber-400" },
    { label: "Webhooks Delivered", value: `${delivered}/${deliveries.length}`, icon: Send, accent: "text-indigo-400" },
  ];
  return (
    <div>
      <SectionHead icon={LayoutDashboard} title={`Welcome, ${me.name || me.merchant_code}`} sub="Your integration status at a glance." />
      <div className="mb-6">
        <DashboardCharts theme="dark" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className={`${card} p-4`}>
              <Icon className={`w-4 h-4 ${s.accent}`} />
              <div className="text-2xl font-black font-mono mt-2">{s.value}</div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mt-0.5">{s.label}</div>
            </div>
          );
        })}
      </div>

      <div className="grid md:grid-cols-2 gap-4 mt-4">
        <div className={`${card} p-5`}>
          <div className="flex items-center gap-2 text-slate-300"><QrCode className="w-4 h-4 text-emerald-400" /><h2 className="font-bold tracking-tight">Assigned QRs</h2></div>
          <div className="mt-3 space-y-2">
            {me.assigned_qrs?.length ? me.assigned_qrs.map((q) => (
              <div key={q.id} className="flex items-center justify-between rounded-lg border border-slate-700 bg-[#0F172A] px-3 py-2">
                <div>
                  <div className="text-sm font-medium">{q.name}</div>
                  <div className="text-xs text-slate-500 font-mono">{q.upi_id}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-slate-400">{money(q.amount_min)} – {money(q.amount_max)}</div>
                  {q.is_active ? <span className="text-[11px] text-emerald-400">Active</span> : <span className="text-[11px] text-slate-500">Inactive</span>}
                </div>
              </div>
            )) : <p className="text-sm text-slate-500">No QRs assigned yet. Ask the branch to link a QR to your merchant account.</p>}
          </div>
        </div>

        <div className={`${card} p-5`}>
          <div className="flex items-center gap-2 text-slate-300"><KeyRound className="w-4 h-4 text-emerald-400" /><h2 className="font-bold tracking-tight">Quick Setup</h2></div>
          <ol className="mt-3 space-y-2 text-sm text-slate-400">
            <li className="flex gap-2"><span className="text-emerald-400 font-bold">1.</span> Copy your API key from <button onClick={() => setSection("integration")} className="text-emerald-400 hover:underline">Integration</button>.</li>
            <li className="flex gap-2"><span className="text-emerald-400 font-bold">2.</span> Set your webhook URL so we can notify you.</li>
            <li className="flex gap-2"><span className="text-emerald-400 font-bold">3.</span> Follow the <button onClick={() => setSection("docs")} className="text-emerald-400 hover:underline">API Docs</button> to go live.</li>
          </ol>
          <Button data-testid="overview-goto-integration" onClick={() => setSection("integration")} className="w-full mt-4 bg-emerald-600 hover:bg-emerald-500 text-white">Manage keys & webhooks <ArrowRight className="w-4 h-4 ml-1" /></Button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Integration ---------------- */
function Integration({ ctx }) {
  const { me, webhook, setWebhook, reveal, busy, rotateKey, rotateSecret, saveWebhook, sendTest } = ctx;
  return (
    <div>
      <SectionHead icon={KeyRound} title="Integration" sub="Manage your API key and webhook configuration." />

      {reveal && (
        <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-4 mb-4">
          <Label className="text-xs text-emerald-300">{reveal.label} — copy now, shown once</Label>
          <div className="flex items-center gap-2 mt-1">
            <code className="flex-1 bg-[#0F172A] border border-slate-700 rounded px-3 py-2 text-sm font-mono break-all">{reveal.value}</code>
            <Button size="sm" variant="outline" className="border-slate-600 bg-transparent h-9" onClick={() => { try { navigator.clipboard.writeText(reveal.value); toast.success("Copied"); } catch (e) { toast.error("Copy manually"); } }}><Copy className="w-4 h-4" /></Button>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <div className={`${card} p-5`}>
          <div className="flex items-center gap-2 text-slate-300"><KeyRound className="w-4 h-4 text-emerald-400" /><h2 className="font-bold tracking-tight">API Key</h2></div>
          <p className="text-sm text-slate-500 mt-1">Bearer token for <code className="font-mono text-xs text-slate-400">/api/v1/*</code>. Rotating invalidates the old key instantly.</p>
          <div className="flex items-center gap-2 mt-3">
            <code data-testid="merchant-api-key-display" className="flex-1 bg-[#0F172A] border border-slate-700 rounded px-3 py-2 text-sm font-mono">{me.api_key_masked}</code>
            <Button data-testid="rotate-api-key-button" onClick={rotateKey} disabled={busy === "key"} variant="outline" className="border-slate-600 bg-transparent text-slate-300 hover:bg-slate-800">{busy === "key" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-3.5 h-3.5 mr-1" />Rotate</>}</Button>
          </div>
        </div>

        <div className={`${card} p-5`}>
          <div className="flex items-center gap-2 text-slate-300"><Webhook className="w-4 h-4 text-emerald-400" /><h2 className="font-bold tracking-tight">Webhook</h2></div>
          <Label className="text-xs text-slate-500 mt-3 block">Webhook URL</Label>
          <div className="flex items-center gap-2 mt-1">
            <Input data-testid="merchant-webhook-url-input" value={webhook} onChange={(e) => setWebhook(e.target.value)} placeholder="https://your.app/webhook" className="bg-[#0F172A] border-slate-700 text-white font-mono text-xs" />
            <Button data-testid="save-webhook-button" onClick={saveWebhook} disabled={busy === "wh"} className="bg-emerald-600 hover:bg-emerald-500 text-white shrink-0">{busy === "wh" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}</Button>
          </div>
          <div className="flex items-center justify-between mt-3">
            <div className="text-xs text-slate-500">Secret: <span className="font-mono text-slate-400">{me.webhook_secret_masked}</span></div>
            <div className="flex gap-2">
              <Button data-testid="rotate-secret-button" onClick={rotateSecret} disabled={busy === "secret"} size="sm" variant="outline" className="border-slate-600 bg-transparent text-slate-300 hover:bg-slate-800 h-8"><RefreshCw className="w-3.5 h-3.5 mr-1" />Secret</Button>
              <Button data-testid="merchant-send-test-webhook-button" onClick={sendTest} disabled={busy === "test"} size="sm" className="bg-emerald-600 hover:bg-emerald-500 text-white h-8"><Send className="w-3.5 h-3.5 mr-1" />Test</Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Submissions ---------------- */
function Submissions({ subs }) {
  return (
    <div>
      <SectionHead icon={ListChecks} title="My Submissions" sub="Every payment claim you've submitted and its live match status." />
      <div className={`${card} p-5`}>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px]">
            <thead><tr className="border-b border-slate-700"><Th></Th><Th>Transaction ID</Th><Th>Reference</Th><Th>Amount</Th><Th>Status</Th><Th>Insight</Th><Th>Submitted</Th></tr></thead>
            <tbody>
              {subs.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-sm text-slate-500">No submissions yet.</td></tr>}
              {subs.map((s) => (
                <ExpandableRow key={s.id} dark rowId={s.id} testPrefix="merchant-sub" items={payinItems(s, false)} cells={[
                  <Td key="t" mono>{s.transaction_id}</Td>,
                  <Td key="r" mono>{s.customer_reference || "—"}</Td>,
                  <Td key="a">{money(s.amount)}</Td>,
                  <Td key="s"><StatusPill status={s.status} /></Td>,
                  <Td key="i"><span className="text-xs text-slate-400">{s.match_insight}</span></Td>,
                  <Td key="d">{timeAgo(s.submitted_at)}</Td>,
                ]} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Charge Backs (view-only) ---------------- */
function ChargeBacks() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/merchant/chargebacks").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  const badge = (s) => s === "Approved" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : s === "Rejected" ? "bg-rose-500/15 text-rose-400 border-rose-500/30" : "bg-amber-500/15 text-amber-400 border-amber-500/30";
  return (
    <div data-testid="merchant-chargebacks">
      <SectionHead icon={RefreshCcw} title="Charge Backs" sub="View-only: charge backs raised against your customers' payments." />
      <div className={`${card} p-5`}>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px]">
            <thead><tr className="border-b border-slate-700"><Th>Transaction ID</Th><Th>Order ID</Th><Th>User ID</Th><Th>Amount</Th><Th>Status</Th><Th>Reason</Th><Th>Raised</Th></tr></thead>
            <tbody>
              {rows === null && <tr><td colSpan={7} className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-slate-500" /></td></tr>}
              {rows?.map((c) => (
                <tr key={c.id} className="border-b border-slate-700/60" data-testid={`merchant-chargeback-row-${c.id}`}>
                  <Td mono>{c.tx_id}</Td><Td mono>{c.order_id || c.customer_reference || "—"}</Td><Td mono>{c.user_id || "—"}</Td>
                  <Td>{money(c.amount)}</Td>
                  <Td><span className={`text-xs px-2 py-0.5 rounded-full border ${badge(c.status)}`}>{c.status}</span></Td>
                  <Td><span className="text-xs text-slate-400">{c.reason || "—"}</span></Td>
                  <Td>{timeAgo(c.requested_at)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {rows?.length === 0 && <p className="py-6 text-center text-sm text-slate-500" data-testid="merchant-chargebacks-empty">No charge backs on your payments.</p>}
      </div>
    </div>
  );
}

/* ---------------- Webhook Logs ---------------- */
function Logs({ deliveries, resend }) {
  return (
    <div>
      <SectionHead icon={ScrollText} title="Webhook Delivery Logs" sub="Every delivery attempt. Failed ones retry automatically — or resend manually." />
      <div className={`${card} p-5`}>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px]">
            <thead><tr className="border-b border-slate-700"><Th>Event</Th><Th>Status</Th><Th>Attempts</Th><Th>Code</Th><Th>When</Th><Th></Th></tr></thead>
            <tbody>
              {deliveries.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-sm text-slate-500">No deliveries yet. Set a webhook URL and send a test from Integration.</td></tr>}
              {deliveries.map((d) => (
                <tr key={d.id} className="border-b border-slate-700/60">
                  <Td mono>{d.event_type}</Td>
                  <Td><DeliveryBadge s={d.status} /></Td>
                  <Td>{d.attempt_count}</Td>
                  <Td>{d.response_code || "—"}</Td>
                  <Td>{timeAgo(d.last_attempted_at || d.created_at)}</Td>
                  <Td><Button size="sm" variant="outline" className="border-slate-600 bg-transparent text-slate-300 hover:bg-slate-800 h-7" onClick={() => resend(d.id)} data-testid={`resend-${d.id}`}>Resend</Button></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
